import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiMapPin, FiClock, FiCpu, FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiExternalLink } = FiIcons;

function Footer() {
  const departments = [
    { name: 'Sales', phone: '(902) 123-4567', hours: 'Mon-Sat 9AM-8PM AST' },
    { name: 'Service', phone: '(902) 123-4568', hours: 'Mon-Fri 7AM-6PM AST' },
    { name: 'Parts', phone: '(902) 123-4569', hours: 'Mon-Fri 8AM-5PM AST' },
    { name: 'Financing', phone: '(902) 123-4570', hours: 'Mon-Sat 9AM-7PM AST' }
  ];

  const languages = ['English', 'Français'];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCpu} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Shawn Ryder Digital</h3>
                <p className="text-blue-200 text-sm">Demo Motors Halifax</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">
              Experience the future of car buying in Halifax with our AI-powered dealership platform. 
              Smarter interactions, faster service, and personalized experiences designed for Maritime drivers.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300">1234 Robie Street, Halifax, NS B3H 3E2</span>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300">halifax@shawnryderdigital.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-400" />
                <a 
                  href="https://shawnryder.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 transition-colors"
                >
                  shawnryder.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Departments */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <h4 className="text-lg font-semibold mb-6">Contact Departments</h4>
            <div className="grid md:grid-cols-2 gap-6">
              {departments.map((dept, index) => (
                <div key={index} className="bg-gray-800 rounded-lg p-4">
                  <h5 className="font-semibold text-blue-400 mb-2">{dept.name}</h5>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-300 text-sm">{dept.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiClock} className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-300 text-sm">{dept.hours}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 bg-blue-900/30 rounded-lg p-4 border border-blue-700">
              <h5 className="font-semibold text-blue-300 mb-2">Halifax AI-Powered Services</h5>
              <p className="text-blue-200 text-sm">
                All our services are enhanced with AI technology from{' '}
                <a 
                  href="https://shawnryder.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:underline"
                >
                  shawnryder.com
                </a>
                {' '}for faster, smarter customer experiences tailored for Maritime drivers.
              </p>
            </div>
          </motion.div>

          {/* AI Features */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-1"
          >
            <h4 className="text-lg font-semibold mb-6">Halifax AI Features</h4>
            <ul className="space-y-3">
              {[
                'Winter Driving Recommendations',
                'Maritime Climate Vehicle Prep',
                'Instant Trade Valuations (CAD)',
                'Smart Service Booking',
                'Nova Scotia MVI Scheduling',
                '24/7 Halifax AI Assistant'
              ].map((feature, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-gray-300 text-sm">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h5 className="font-semibold mb-3">Language Support</h5>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-xs"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <a 
                href="https://shawnryder.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors text-sm"
              >
                <span>Learn more about our Halifax AI technology</span>
                <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="border-t border-gray-800 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiFacebook} className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiTwitter} className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiInstagram} className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <SafeIcon icon={FiLinkedin} className="w-5 h-5" />
              </a>
            </div>

            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-gray-400 text-sm">
                © 2024 Shawn Ryder Digital Demo Motors Halifax. All rights reserved.
              </p>
              <div className="flex space-x-4 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Accessibility</a>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-xs">
              Powered by{' '}
              <a 
                href="https://shawnryder.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 font-medium hover:text-blue-300 transition-colors"
              >
                Shawn Ryder Digital AI
              </a>
              {' '}• Transforming Halifax automotive retail through intelligent automation • OpenAI Integration
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}

export default Footer;